import { useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import useEvent from 'react-use-event-hook'

import { ArrowDownTrayIcon } from '@gto/icons-react/outline/arrow-down-tray'
import { DocumentIcon } from '@gto/icons-react/outline/document'
import { EllipsisVerticalIcon } from '@gto/icons-react/outline/ellipsis-vertical'
import { FilmIcon } from '@gto/icons-react/outline/film'
import { PencilIcon } from '@gto/icons-react/outline/pencil'
import { TrashIcon } from '@gto/icons-react/outline/trash'
import { IconButton } from '@gto/ui/icon-button'
import { Menu } from '@gto/ui/menu'
import { Paper } from '@gto/ui/paper'
import { Typography } from '@gto/ui/typography'

import { formatFileSize } from '@/utils/format-file-size'

import { CATEGORY_ICON, isImage, isVideo } from './asset-media'
import { ExtensionBadge } from './extension-badge'

type AssetCardProps = {
  name: string
  category: string
  fileSize: number
  contentType: string
  downloadUrl: string | null
  onPreview?: () => void
  onDownload?: () => void
  onRename?: () => void
  onDelete?: () => void
}

// Cover-cropped media inside the fixed-aspect thumb, with a subtle hover zoom.
const THUMB_MEDIA_CLASSES =
  'size-full object-cover transition-transform duration-shorter ease-out group-hover:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover:scale-100'

type VideoThumbnailProps = { src: string; onError: () => void }

const VideoThumbnail = ({ src, onError }: VideoThumbnailProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [ready, setReady] = useState(false)
  // Stable identity so the effect re-runs only when src changes — re-running
  // the probe on every parent render would re-download the video.
  const handleError = useEvent(onError)

  useEffect(
    function captureVideoThumbnail() {
      const video = document.createElement('video')
      video.crossOrigin = 'anonymous'
      video.preload = 'metadata'
      video.muted = true
      video.playsInline = true

      video.onloadeddata = () => {
        video.currentTime = 1
      }

      video.onseeked = () => {
        const canvas = canvasRef.current
        if (!canvas) {
          return
        }
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        canvas.getContext('2d')?.drawImage(video, 0, 0)
        setReady(true)
        video.remove()
      }

      video.onerror = () => {
        handleError()
        video.remove()
      }

      video.src = src
    },
    [src, handleError],
  )

  return (
    <>
      {!ready && (
        <div className="flex size-full items-center justify-center">
          <FilmIcon className="size-8 text-text-secondary" />
        </div>
      )}
      <canvas
        ref={canvasRef}
        className={THUMB_MEDIA_CLASSES}
        style={{ display: ready ? 'block' : 'none' }}
      />
      {ready && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="flex size-10 items-center justify-center rounded-full bg-black/55">
            <svg
              fill="white"
              height={20}
              viewBox="0 0 24 24"
              width={20}
            >
              <path d="M8 5.14v14l11-7-11-7z" />
            </svg>
          </span>
        </div>
      )}
    </>
  )
}

export const AssetCard = ({
  name,
  category,
  fileSize,
  contentType,
  downloadUrl,
  onPreview,
  onDownload,
  onRename,
  onDelete,
}: AssetCardProps) => {
  const { formatMessage } = useIntl()
  const Icon = CATEGORY_ICON[category] ?? DocumentIcon
  const extension = contentType.split('/').pop()?.toUpperCase() ?? ''
  const [broken, setBroken] = useState(false)

  const hasAdminActions = onRename != null || onDelete != null
  const canDownload = onDownload != null || downloadUrl != null
  const hasActions = hasAdminActions || canDownload
  const showMediaThumb =
    downloadUrl != null && !broken && (isImage(contentType) || isVideo(contentType))

  const handleDownload = () => {
    if (onDownload != null) {
      onDownload()
    } else if (downloadUrl != null) {
      window.open(downloadUrl, '_blank')
    }
  }

  return (
    <Paper
      className="group relative flex flex-col overflow-hidden rounded-md border border-transparent transition-[border-color,box-shadow] duration-shorter focus-within:border-divider hover:border-divider hover:shadow-4"
      elevation="2"
    >
      {/* Stretched overlay button = the only preview trigger: a native
          keyboard path (Tab → Enter/Space) with no click-routing logic. The
          actions sit at z-[2] above it, so they remain independently
          clickable siblings — not nested interactives. */}
      {onPreview != null && (
        <button
          aria-label={formatMessage({ id: 'assets.previewAction' }, { name })}
          className="absolute inset-0 z-[1] cursor-pointer rounded-md outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-main"
          type="button"
          onClick={onPreview}
        />
      )}

      <div className="relative aspect-video w-full overflow-hidden bg-paper-elevation3">
        {showMediaThumb && isImage(contentType) ? (
          // alt="" — the card title right below names the asset; a duplicate
          // alt is screen-reader noise.
          <img
            alt=""
            className={THUMB_MEDIA_CLASSES}
            decoding="async"
            loading="lazy"
            src={downloadUrl}
            onError={() => setBroken(true)}
          />
        ) : showMediaThumb ? (
          <VideoThumbnail
            src={downloadUrl}
            onError={() => setBroken(true)}
          />
        ) : (
          <div className="flex size-full flex-col items-center justify-center gap-2">
            <Icon className="size-8 text-text-secondary" />
            <ExtensionBadge>{extension}</ExtensionBadge>
          </div>
        )}

        {/* Scrim keeps the white action icons readable on busy thumbnails. */}
        {hasActions && showMediaThumb && (
          <div
            aria-hidden
            className="bg-gradient-to-b pointer-events-none absolute inset-x-0 top-0 h-10 from-black/50 to-transparent opacity-0 transition-opacity duration-shorter group-focus-within:opacity-100 group-hover:opacity-100 md:opacity-100"
          />
        )}

        {/* Always visible at md: and below — touch devices have no hover. */}
        {hasActions && (
          <div className="absolute right-1 top-1 z-[2] opacity-0 transition-opacity duration-shorter group-focus-within:opacity-100 group-hover:opacity-100 md:opacity-100">
            {hasAdminActions ? (
              <Menu.Root>
                <Menu.Trigger
                  render={
                    <IconButton
                      aria-label={formatMessage({ id: 'common.assetActions' })}
                      className="text-white hover:bg-black/30"
                      size="small"
                    >
                      <EllipsisVerticalIcon
                        height={16}
                        width={16}
                      />
                    </IconButton>
                  }
                />
                <Menu.Portal>
                  <Menu.Positioner align="end">
                    <Menu.Popup className="min-w-[160px]">
                      {canDownload && (
                        <Menu.Item onClick={handleDownload}>
                          <ArrowDownTrayIcon
                            height={16}
                            width={16}
                          />
                          <FormattedMessage id="assets.download" />
                        </Menu.Item>
                      )}
                      {onRename != null && (
                        <Menu.Item onClick={onRename}>
                          <PencilIcon
                            height={16}
                            width={16}
                          />
                          <FormattedMessage id="admin.assets.rename" />
                        </Menu.Item>
                      )}
                      {onDelete != null && (canDownload || onRename != null) && <Menu.Separator />}
                      {onDelete != null && (
                        <Menu.Item onClick={onDelete}>
                          <TrashIcon
                            height={16}
                            width={16}
                          />
                          <FormattedMessage id="admin.common.delete" />
                        </Menu.Item>
                      )}
                    </Menu.Popup>
                  </Menu.Positioner>
                </Menu.Portal>
              </Menu.Root>
            ) : (
              // Download is the only action on the partner page — a one-item
              // kebab would be a pointless extra click.
              <IconButton
                aria-label={formatMessage({ id: 'assets.download' })}
                className="text-white hover:bg-black/30"
                size="small"
                onClick={handleDownload}
              >
                <ArrowDownTrayIcon
                  height={16}
                  width={16}
                />
              </IconButton>
            )}
          </div>
        )}
      </div>

      <div className="flex min-w-0 flex-col gap-0.5 px-3 py-2">
        <Typography
          noWrap
          className="font-medium"
          title={name}
          variant="body2"
        >
          {name}
        </Typography>
        <Typography
          className="truncate"
          color="text.secondary"
          variant="caption"
        >
          {`${extension} · ${formatFileSize(fileSize)}`}
        </Typography>
      </div>
    </Paper>
  )
}
